// Календарный heatmap: столбцы недель, 7 строк дней.
// Ступень интенсивности задаётся классом .heat-0..4 — сами цвета живут
// в styles.css и потому корректны в обеих темах.
// Tap по ячейке показывает дату + сумму.

import { useState } from 'react';
import { formatWithCurrency } from '../../lib/money';

export type HeatmapDay = {
  date: string; // YYYY-MM-DD
  amountMinor: string;
};

type Props = {
  days: HeatmapDay[]; // отсортированы по дате
  currency: string;
};

// Распределяем дни по сетке: первая колонка — самая ранняя неделя, последняя — текущая.
// Postgres EXTRACT(DOW): 0=Sun..6=Sat. Мы ставим в строку с ru-сдвигом: 0=Mon..6=Sun.
function dowRu(date: Date): number {
  // 0=Sun..6=Sat → 0=Mon..6=Sun
  const j = date.getUTCDay();
  return j === 0 ? 6 : j - 1;
}

const CELL = 12;
const GAP = 2;

export function CalendarHeatmap({ days, currency }: Props): JSX.Element {
  const [hovered, setHovered] = useState<HeatmapDay | null>(null);

  if (days.length === 0) {
    return <div className="empty-mini">Нет данных</div>;
  }

  const map = new Map(days.map((d) => [d.date, d]));
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return <div className="empty-mini">Нет данных</div>;
  const firstDate = new Date(first.date + 'T00:00:00Z');
  const lastDate = new Date(last.date + 'T00:00:00Z');

  // Стартовая воскресенье/понедельник — выровнять по строкам.
  const firstDow = dowRu(firstDate);
  const gridStart = new Date(firstDate.getTime() - firstDow * 24 * 60 * 60 * 1000);

  const cellsCount = Math.ceil(
    (lastDate.getTime() - gridStart.getTime()) / (24 * 60 * 60 * 1000) + 1,
  );
  const weeks = Math.ceil(cellsCount / 7);

  const max = Math.max(...days.map((d) => Number(BigInt(d.amountMinor) / 100n)), 1);

  function intensity(amountMinor: string): number {
    const v = Number(BigInt(amountMinor) / 100n);
    if (v === 0) return 0;
    const ratio = v / max;
    if (ratio >= 0.75) return 4;
    if (ratio >= 0.5) return 3;
    if (ratio >= 0.25) return 2;
    return 1;
  }

  const cells: Array<{
    x: number;
    y: number;
    day: HeatmapDay | null;
    date: string;
    inRange: boolean;
  }> = [];
  for (let i = 0; i < cellsCount; i++) {
    const d = new Date(gridStart.getTime() + i * 24 * 60 * 60 * 1000);
    const ds = d.toISOString().slice(0, 10);
    const x = Math.floor(i / 7);
    const y = i % 7;
    const inRange = d.getTime() >= firstDate.getTime() && d.getTime() <= lastDate.getTime();
    const day = map.get(ds) ?? null;
    cells.push({ x, y, day: day ?? (inRange ? { date: ds, amountMinor: '0' } : null), date: ds, inRange });
  }

  const width = weeks * (CELL + GAP);
  const height = 7 * (CELL + GAP);

  return (
    <div className="cal-heatmap">
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
        {cells.map((cell) => {
          if (!cell.inRange || !cell.day) return null;
          const level = intensity(cell.day.amountMinor);
          return (
            <rect
              key={cell.date}
              x={cell.x * (CELL + GAP)}
              y={cell.y * (CELL + GAP)}
              width={CELL}
              height={CELL}
              rx={2}
              className={`heat-${level}`}
              onMouseEnter={() => setHovered(cell.day)}
              onMouseLeave={() => setHovered(null)}
              onTouchStart={() => setHovered(cell.day)}
              style={{ cursor: 'pointer' }}
            />
          );
        })}
      </svg>
      {hovered ? (
        <div className="heatmap-hint">
          <strong>{formatRu(hovered.date)}</strong>:{' '}
          {Number(hovered.amountMinor) === 0
            ? 'нет трат'
            : formatWithCurrency(hovered.amountMinor, currency)}
        </div>
      ) : (
        <div className="heatmap-hint muted">Нажмите на ячейку — увидите дату и сумму</div>
      )}
    </div>
  );
}

function formatRu(yyyyMmDd: string): string {
  const [, m, d] = yyyyMmDd.split('-');
  if (!m || !d) return yyyyMmDd;
  const months = [
    'янв', 'фев', 'мар', 'апр', 'мая', 'июн',
    'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
  ];
  return `${Number(d)} ${months[Number(m) - 1] ?? m}`;
}
